import { CardContent, CardShellEcho } from '@/components/cards/common';
import NoDataMessageEcho from '@/components/states/NoDataMessageEcho';
import { Flex, theme } from 'antd';
import { useEffect, useMemo, useState } from 'react';
import { GenderChartEcho } from './charts/GenderChart/GenderChartEcho';
import { useGenderPlaysQuery } from '@/services/revenueApi';
import { GenderPlaysResponse, SelectedPlatform } from '@/types/api/gender';
import { mixpanelTrack, RevenueDashboardWidget } from '@/utils';
import { MIXPANEL_EVENT } from '@/utils/mixpanelUtils/events';
import { getFirstDisplayPlatform } from '@/utils/platformUtils';
import GenderModalEcho from './GenderModalEcho';
import DataAvailabilityBanner from './DataAvailabilityBanner';
import useAvailabilityQuery from './useAvailabilityQuery';

const GenderCardEcho = ({ className }: { className?: string }) => {
  const { showBanner, hideContent, queryResult } = useAvailabilityQuery(
    'gender',
    useGenderPlaysQuery
  );

  const { data: genderPlays } = queryResult;

  const platforms = useMemo(() => {
    return [...new Set(genderPlays?.plays?.map((play) => play.platform) ?? [])];
  }, [genderPlays]);

  type SelectedPlatform = (typeof platforms)[number];

  const [selectedPlatform, setSelectedPlatform] = useState<SelectedPlatform | null>(
    platforms?.[0] ?? null
  );

  useEffect(() => {
    setSelectedPlatform(
      getFirstDisplayPlatform({ plays: platforms.map((p) => ({ platform: p })) })
    );
  }, [platforms]);

  const handlePlatformChange = (platform: SelectedPlatform) => {
    setSelectedPlatform(platform);

    mixpanelTrack(MIXPANEL_EVENT.audience.demographics.platformRadioSelected, {
      page: 'audience',
      widget: RevenueDashboardWidget.GenderDemographics,
      platform: platform,
      demographics: 'gender',
    });
  };

  return (
    <CardShellEcho
      className={className}
      title="Gender"
      headerOptions={genderPlays?.plays?.length && <GenderModalEcho />}
    >
      {showBanner && <DataAvailabilityBanner dataType="gender" />}
      {!hideContent && (
        <CardContent
          variant="echo"
          {...queryResult}
          renderData={(genderData: GenderPlaysResponse) => (
            <Data
              data={genderData}
              selectedPlatform={selectedPlatform}
              handlePlatformChange={handlePlatformChange}
            />
          )}
        />
      )}
    </CardShellEcho>
  );
};

const Data = ({
  data,
  selectedPlatform,
  handlePlatformChange,
}: {
  data: GenderPlaysResponse;
  selectedPlatform: SelectedPlatform<typeof data> | null;
  handlePlatformChange: (platform: SelectedPlatform<typeof data>) => void;
}) => {
  const { token } = theme.useToken();

  if (data?.plays?.length < 1 || selectedPlatform === null) {
    return <NoDataMessageEcho dataType="gender data" />;
  }

  return (
    <Flex
      vertical
      justify="space-between"
      gap={token.margin}
      style={{ flex: 1, marginTop: token.marginLG }}
    >
      <GenderChartEcho
        data={data}
        selectedPlatform={selectedPlatform}
        handlePlatformChange={handlePlatformChange}
      />
    </Flex>
  );
};

export default GenderCardEcho;
